
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Users, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FriendsSearch from "@/components/friends/FriendsSearch";

/**
 * Interface for friend data
 */
interface Friend {
  id: string;
  username: string;
  avatar_url: string | null;
  points: number;
  rank: number;
}

/**
 * Friends Page Component
 * 
 * Main page for managing friends, including search, requests, and friends list.
 * Provides tabs for different friend-related functionality.
 */
const Friends = () => {
  // State for friends list
  const [friends, setFriends] = useState<Friend[]>([]);
  const [isLoadingFriends, setIsLoadingFriends] = useState(true);
  const { toast } = useToast();

  /**
   * Load the current user's friends list
   */
  const loadFriends = async () => {
    try {
      setIsLoadingFriends(true);
      console.log('Loading friends list...');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Get friends from leaderboard view where is_friend is true
      const { data: friendsData, error } = await supabase
        .from('leaderboard_view')
        .select('*')
        .eq('is_friend', true)
        .order('points', { ascending: false });
      
      if (error) {
        console.error('Error loading friends:', error);
        toast({
          title: "Error loading friends",
          description: "Could not load your friends list. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Transform the data to match our Friend interface
      const transformedFriends: Friend[] = friendsData?.map(friend => ({
        id: friend.user_id || '',
        username: friend.username || '',
        avatar_url: friend.avatar_url,
        points: friend.points || 0,
        rank: friend.rank || 0
      })) || [];
      
      setFriends(transformedFriends);
      console.log('Loaded friends:', transformedFriends);
    } catch (error) {
      console.error('Error in loadFriends:', error);
    } finally {
      setIsLoadingFriends(false);
    }
  };

  // Load friends when component mounts
  useEffect(() => {
    loadFriends();
  }, []);

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">Friends</h1>
      </div>

      {/* Friends Management Tabs */}
      <Tabs defaultValue="search" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="search" className="text-sm">
            <Users className="h-4 w-4 mr-2" />
            Find & Manage
          </TabsTrigger>
          <TabsTrigger value="friends" className="text-sm">
            <UserCheck className="h-4 w-4 mr-2" />
            My Friends ({friends.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Find & Manage Friends Tab */}
        <TabsContent value="search" className="mt-0">
          <FriendsSearch />
        </TabsContent>
        
        {/* My Friends Tab */}
        <TabsContent value="friends" className="mt-0">
          <Card className="bg-background rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <CardHeader className="p-6 border-b border-gray-100">
              <CardTitle className="text-2xl font-semibold text-gray-900">
                Your Friends
              </CardTitle>
              <p className="text-gray-600 mt-1">
                {friends.length > 0 
                  ? `You have ${friends.length} friend${friends.length !== 1 ? 's' : ''}`
                  : "You haven't added any friends yet"
                }
              </p>
            </CardHeader>
            
            <CardContent className="p-0">
              {isLoadingFriends ? (
                // Loading state
                <div className="p-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg"></div>
                    ))}
                  </div>
                </div>
              ) : friends.length > 0 ? (
                // Friends list
                <div className="divide-y">
                  {friends.map((friend, index) => (
                    <div 
                      key={friend.id}
                      className={`p-4 flex items-center justify-between ${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Friend avatar/initial */}
                        <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-medium">
                          {friend.username.charAt(0).toUpperCase()}
                        </div>
                        
                        {/* Friend info */}
                        <div>
                          <div className="font-medium">{friend.username}</div>
                          <div className="text-sm text-gray-500">
                            Rank #{friend.rank} • {friend.points} points
                          </div>
                        </div>
                      </div>
                      
                      {/* Friend rank badge */}
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          #{friend.rank}
                        </div>
                        <div className="text-xs text-gray-500">
                          {friend.points} pts
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Empty state
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No friends yet</p>
                  <p>Start adding friends to see them here and compete on the leaderboard!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Friends;
